import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  DEFAULT_SELECTED_EFFORT_LABEL,
  EFFORT_MANAGED_BY_SCALAR_LABEL,
  ENABLE_DOCUMENT_AUTO_CLASSIFICATION,
  ENABLE_MCP,
  FIRM_SETTINGS,
  MAXIMUM_ALLOWED_EFFORT_LABEL,
  SCALAR_AI_SETTINGS_PAGE_TITLE,
} from 'common/constants/firms';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { GridSkeleton } from 'components/Grid';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import ScalarIcon from 'icons/Scalar';
import { buildFirmSettingsNavigationItems } from 'pages/Firms/firmSettingsNavigation';
import { useReadUserAccessLevel } from 'services/hooks';
import { useGetFirmScalarAiSettings, useUpdateFirmScalarAiSettings } from 'services/hooks/scalarAi';

// Effort ladder for the SETTINGS dropdowns only. The scalar icon here marks the
// staff-only tiers (a fixed set) - deliberately different from the job-create
// selector, where the server-computed flag means "beyond this firm's max".
// Everyone below Scalar staff renders only the first two entries and never
// learns the rest exist.
const EFFORT_SETTINGS_OPTIONS = [
  { value: 'fastest', label: 'Fastest', staffOnly: false },
  { value: 'standard', label: 'Standard', staffOnly: false },
  { value: 'balanced', label: 'Balanced', staffOnly: true },
  { value: 'high', label: 'High', staffOnly: true },
  { value: 'maximum', label: 'Maximum', staffOnly: true },
] as const;

type EffortSettingsOption = (typeof EFFORT_SETTINGS_OPTIONS)[number];

const EFFORT_ORDER: string[] = EFFORT_SETTINGS_OPTIONS.map(option => option.value);

const effortRank = (value: string) => EFFORT_ORDER.indexOf(value);

interface EffortSelectProps {
  labelId: string;
  label: string;
  value: string;
  options: readonly EffortSettingsOption[];
  disabled: boolean;
  // The firm sits on a tier this user may not see: show a neutral placeholder
  // instead of an out-of-range value, and take the control out of play.
  managed: boolean;
  maxRank?: number;
  onChange: (value: string) => void;
}

const EffortSelect = ({ labelId, label, value, options, disabled, managed, maxRank, onChange }: EffortSelectProps) => (
  <FormControl fullWidth>
    <InputLabel id={labelId} shrink={managed || undefined}>
      {label}
    </InputLabel>
    <Select
      labelId={labelId}
      label={label}
      value={managed ? '' : value}
      displayEmpty={managed}
      renderValue={managed ? () => EFFORT_MANAGED_BY_SCALAR_LABEL : undefined}
      disabled={disabled || managed}
      onChange={event => onChange(String(event.target.value))}>
      {options.map(option => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={maxRank !== undefined && effortRank(option.value) > maxRank}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {option.label}
            {option.staffOnly && (
              <Box component="span" data-testid="effort-staff-tier-icon" sx={{ display: 'inline-flex' }}>
                <ScalarIcon />
              </Box>
            )}
          </Box>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const ScalarAiSettings = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';
  // Two DIFFERENT gates, deliberately not merged:
  // - the relocated checkboxes were staff-only on Firm Profile (is_staff ONLY);
  //   the move must not widen who can see them.
  // - the full effort ladder is Scalar-internal, i.e. staff OR superuser.
  const isUserStaff = Boolean(user?.is_staff);
  const { isAdminOrStaffOrSuperuser, isStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { data: firm, isLoading } = useGetFirmScalarAiSettings(firmId);
  const { updateFirmScalarAiSettings, isPending } = useUpdateFirmScalarAiSettings();

  const [maxAllowedEffort, setMaxAllowedEffort] = useState('fastest');
  const [defaultEffort, setDefaultEffort] = useState('fastest');
  const [autoClassificationEnabled, setAutoClassificationEnabled] = useState(false);
  const [mcpEnabled, setMcpEnabled] = useState(false);

  useEffect(() => {
    if (firm) {
      setMaxAllowedEffort(firm.max_allowed_effort ?? 'fastest');
      setDefaultEffort(firm.default_effort ?? 'fastest');
      setAutoClassificationEnabled(firm.document_auto_classification_enabled ?? false);
      setMcpEnabled(firm.is_mcp_enabled ?? false);
    }
  }, [firm]);

  const visibleOptions = useMemo(
    () => EFFORT_SETTINGS_OPTIONS.filter(option => isStaffOrSuperuser || !option.staffOnly),
    [isStaffOrSuperuser]
  );

  // The firm is parked above this user's ladder. Both dropdowns go neutral, and
  // the save drops both effort fields, so a Save can never echo back - or
  // silently downgrade - a tier the user was never allowed to see.
  const isEffortManagedByScalar = useMemo(
    () => [maxAllowedEffort, defaultEffort].some(value => !visibleOptions.some(option => option.value === value)),
    [maxAllowedEffort, defaultEffort, visibleOptions]
  );

  // Client-side mirror of the server's default <= max rule: lowering the max
  // pulls the default down with it, and higher default options are disabled.
  const handleMaxAllowedEffortChange = useCallback((nextMax: string) => {
    setMaxAllowedEffort(nextMax);
    setDefaultEffort(current => (effortRank(current) > effortRank(nextMax) ? nextMax : current));
  }, []);

  const saveScalarAiSettings = useCallback(async () => {
    // No firm in hand (still loading, or the read failed) means there is no
    // baseline to write back - saving now would PATCH the component defaults.
    if (!firmId || !firm) {
      return;
    }
    const updatedFirm = await updateFirmScalarAiSettings(firmId, {
      ...(isEffortManagedByScalar ? {} : { max_allowed_effort: maxAllowedEffort, default_effort: defaultEffort }),
      ...(isUserStaff
        ? {
          document_auto_classification_enabled: autoClassificationEnabled,
          is_mcp_enabled: mcpEnabled,
        }
        : {}),
    });
    if (updatedFirm) {
      enqueueSnackbar('Scalar AI settings successfully saved', { variant: 'success' });
    }
  }, [
    firmId,
    firm,
    isEffortManagedByScalar,
    maxAllowedEffort,
    defaultEffort,
    isUserStaff,
    autoClassificationEnabled,
    mcpEnabled,
    updateFirmScalarAiSettings,
    enqueueSnackbar,
  ]);

  const secondaryNavigation = useMemo(
    () => ({
      title: FIRM_SETTINGS,
      items: buildFirmSettingsNavigationItems(firmSlug, {
        dailyNavEnabled: Boolean(firmInfo?.daily_nav_enabled),
      }),
      actions: isAdminOrStaffOrSuperuser
        ? [
          {
            type: 'button' as const,
            key: 'firm-scalar-ai-settings-save',
            label: 'Save',
            variant: 'positive' as const,
            onClick: saveScalarAiSettings,
            disabled: !firmId || !firm || isPending,
          },
        ]
        : [],
    }),
    [firmSlug, firmInfo?.daily_nav_enabled, isAdminOrStaffOrSuperuser, saveScalarAiSettings, firmId, firm, isPending]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  if (isLoading || !firm) {
    return <GridSkeleton />;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">{SCALAR_AI_SETTINGS_PAGE_TITLE}</Typography>
      <EffortSelect
        labelId="max-allowed-effort-label"
        label={MAXIMUM_ALLOWED_EFFORT_LABEL}
        value={maxAllowedEffort}
        options={visibleOptions}
        disabled={!isAdminOrStaffOrSuperuser}
        managed={isEffortManagedByScalar}
        onChange={handleMaxAllowedEffortChange}
      />
      <EffortSelect
        labelId="default-effort-label"
        label={DEFAULT_SELECTED_EFFORT_LABEL}
        value={defaultEffort}
        options={visibleOptions}
        disabled={!isAdminOrStaffOrSuperuser}
        managed={isEffortManagedByScalar}
        maxRank={effortRank(maxAllowedEffort)}
        onChange={setDefaultEffort}
      />
      {isUserStaff && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoClassificationEnabled}
                onChange={event => setAutoClassificationEnabled(event.target.checked)}
                name="document_auto_classification_enabled"
              />
            }
            label={ENABLE_DOCUMENT_AUTO_CLASSIFICATION}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={mcpEnabled}
                onChange={event => setMcpEnabled(event.target.checked)}
                name="is_mcp_enabled"
                id="is_mcp_enabled"
              />
            }
            label={ENABLE_MCP}
          />
        </Box>
      )}
    </Box>
  );
};

export default ScalarAiSettings;
